// ЕДИНСТВЕННЫЙ HTTP-клиент приложения. Все запросы к бэкенду — только через apiGet/apiSend.
// Знает: базу API, X-Access-Token (токен ПРИЛОЖЕНИЯ, не пользователя), язык, конверт ошибок {detail, code}.
// Сессия (Bearer + single-flight refresh) подключится сюда на этапе входа через Telegram.
import { deadline } from './deadline';
import { lang } from '../i18n/i18n';

// dev: относительный адрес → прокси Vite; prod: абсолютная база из окружения сборки.
const API_BASE = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE ?? 'https://api.aplback.com');
const ACCESS_TOKEN: string = import.meta.env.VITE_API_ACCESS_TOKEN ?? '';

/**
 * Идентификатор установки (X-Device-Id, 8–64 знака): опознание гостя, без него голос в опросе не принимается.
 * localStorage может быть недоступен (приватный режим) — тогда id живёт до перезапуска.
 */
export const DEVICE_ID: string = (() => {
  const key = 'cricket.device_id';
  try {
    const saved = localStorage.getItem(key);
    if (saved && /^[A-Za-z0-9-]{8,64}$/.test(saved)) return saved;
    const fresh = crypto.randomUUID();
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
})();

/**
 * Способности сборки (заголовок X-Client-Capabilities, гид бэкенда §1.9): сервер меняет ответ только для того, кто
 * объявил. `cricket_entity_tabs` (HQ-207) — «Рекорды» и «Новости» команды вкладками: блок `news` уходит из обзора.
 * `game_world_order_v1` (HQ-788) не объявляем: это способность магазинной сборки, Mini App порядок мира волны 22.10
 * сервер отдаёт без неё (HQ-785, access.world_order).
 * Неизвестные серверу значения он молча пропускает. Заголовок обязан быть в CORS_ALLOW_HEADERS бэкенда (preflight).
 */
export const CLIENT_CAPABILITIES: readonly string[] = ['cricket_entity_tabs'];

export type ApiErrorKind = 'network' | 'http';

/** Ошибка запроса. Ветвиться только по `code` (detail не локализован) или по `kind`/`status`. */
export class ApiError extends Error {
  constructor(
    readonly kind: ApiErrorKind,
    readonly status: number,
    readonly code: string | null,
    readonly retryAfterSeconds: number | null = null,
    /** Тело ошибки (например, limit у favorite_limit_exceeded). */
    readonly payload: Record<string, unknown> | null = null,
  ) {
    super(`${kind} ${status} ${code ?? ''}`.trim());
  }
}

/** Свои параметры доклеиваем к адресу сервера, его собственные не трогаем. `+` кодируется как %2B. */
export function withParams(path: string, params: Record<string, string | number | undefined>): string {
  const [base, query = ''] = path.split('?', 2) as [string, string?];
  const existing = new URLSearchParams(query);
  const parts = query ? [query] : [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || existing.has(key)) continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `${base}?${parts.join('&')}` : base;
}

/** Источник Bearer для запросов с auth (подключает session.ts; так клиент не зависит от сессии). */
export interface BearerProvider {
  token(): Promise<string>;
  /** 401 на запросе с этим токеном: сбросить его, следующий token() обновит (single-flight). */
  invalidate(rejected: string): void;
}
let bearer: BearerProvider | null = null;
export function setBearerProvider(provider: BearerProvider): void { bearer = provider; }

/** keepalive — запрос переживает закрытие окна (fetch keepalive, тело ≤ 64 КБ): отправка на уходе. */
export interface RequestOptions { body?: unknown; signal?: AbortSignal; auth?: boolean; headers?: Record<string, string>; keepalive?: boolean }

/** Запрос с Bearer: на 401 — одно обновление токена и ОДИН повтор (решение по статусу, код не важен). */
export async function apiRequest<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  if (!opts.auth || !bearer) return request<T>(method, path, opts.body, opts.signal, opts.headers, opts.keepalive);
  const first = await bearer.token();
  try {
    return await request<T>(method, path, opts.body, opts.signal, { ...opts.headers, Authorization: `Bearer ${first}` }, opts.keepalive);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
    bearer.invalidate(first);
    const second = await bearer.token();
    return request<T>(method, path, opts.body, opts.signal, { ...opts.headers, Authorization: `Bearer ${second}` }, opts.keepalive);
  }
}

/** Исход пишущего запроса — для аналитики (src/analytics): клиент о ней не знает, только сообщает слушателю. */
export interface ApiAction { method: string; path: string; status: number; code: string | null; reason?: unknown; /** ответ */ body?: unknown; /** что отправили */ sent?: unknown }
let actionListener: ((a: ApiAction) => void) | null = null;
export function setActionListener(fn: ((a: ApiAction) => void) | null): void { actionListener = fn; }
function report(a: ApiAction): void {
  if (!actionListener || a.method === 'GET') return;
  try { actionListener(a); } catch { /* аналитика не роняет запрос */ }
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal, extra?: Record<string, string>, keepalive?: boolean): Promise<T> {
  // keepalive (отправка на уходе) — без тайм-аута: запрос должен пережить окно, обрывать его незачем.
  const limit = keepalive ? null : deadline(signal);
  try {
    return await exchange<T>(method, path, body, limit?.signal ?? signal, extra, keepalive);
  } catch (error) {
    if (!limit?.timedOut() || error instanceof ApiError) throw error;
    report({ method, path, status: 0, code: null, sent: body });
    throw new ApiError('network', 0, null);
  } finally {
    limit?.dispose();
  }
}

/**
 * Тело успешного ответа (HQ-1396). Неразбираемое тело 2xx — HTML гостевого Wi-Fi, прокси, captive portal — это сбой
 * сети, не ответ сервера: ApiError('network') вместо голого SyntaxError, чтобы сработали повтор запросов (shouldRetry)
 * и плашка «нет сети». Мутации по-прежнему не повторяются. Отмена (AbortError, в т. ч. тайм-аут) остаётся отменой.
 */
async function readBody(response: Response, method: string, path: string, sent: unknown): Promise<unknown> {
  try {
    return await response.json();
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    report({ method, path, status: 0, code: null, sent });
    throw new ApiError('network', response.status, null);
  }
}

async function exchange<T>(method: string, path: string, body?: unknown, signal?: AbortSignal, extra?: Record<string, string>, keepalive?: boolean): Promise<T> {
  const url = API_BASE + withParams(path, { lang });
  // X-Platform отличает наш трафик в аналитике бэкенда (UserDailyActivity) от мобильных клиентов.
  const headers: Record<string, string> = {
    'X-Access-Token': ACCESS_TOKEN, 'X-Platform': 'telegram', 'X-Device-Id': DEVICE_ID,
    'X-Client-Capabilities': CLIENT_CAPABILITIES.join(','), Accept: 'application/json',
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  Object.assign(headers, extra);
  let response: Response;
  try {
    response = await fetch(url, { method, headers, signal, body: body === undefined ? undefined : JSON.stringify(body), ...(keepalive ? { keepalive } : {}) });
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    report({ method, path, status: 0, code: null, sent: body });
    throw new ApiError('network', 0, null);
  }
  if (!response.ok) {
    let code: string | null = null;
    let retryAfter: number | null = Number(response.headers.get('Retry-After')) || null;
    let payload: Record<string, unknown> | null = null;
    try {
      payload = (await response.json()) as Record<string, unknown>;
      code = typeof payload.code === 'string' ? payload.code : null;
      retryAfter = typeof payload.retry_after_seconds === 'number' ? payload.retry_after_seconds : retryAfter;
    } catch { /* тело не JSON — остаётся только статус */ }
    report({ method, path, status: response.status, code, reason: payload?.reason, sent: body });
    throw new ApiError('http', response.status, code, retryAfter, payload);
  }
  if (response.status === 204) { report({ method, path, status: 204, code: null, sent: body }); return undefined as T; }
  const data = (await readBody(response, method, path, body)) as T;
  report({ method, path, status: response.status, code: null, body: data, sent: body });
  return data;
}

/** headers — свои заголовки запроса (X-Timezone экрана матча, HQ-2336) поверх общих. */
export function apiGet<T>(path: string, signal?: AbortSignal, headers?: Record<string, string>): Promise<T> {
  return request<T>('GET', path, undefined, signal, headers);
}

export function apiSend<T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  return request<T>(method, path, body);
}

/** Ретраить только сеть и 5xx/429; 4xx — это ответ, не сбой. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (!(error instanceof ApiError)) return false;
  return error.kind === 'network' || error.status >= 500 || error.status === 429;
}
